

export function speakWithFemaleVoice(text: string) {
  // Check if speech synthesis is available
  if (!('speechSynthesis' in window)) {
    console.warn('Speech synthesis not supported in this browser');
    return;
  }

  function speak() {
    try {
      const utterance = new window.SpeechSynthesisUtterance(text);
      
      // Set default properties
      utterance.lang = 'en-US';
      utterance.rate = 0.9; // Slightly slower for better clarity
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      const voices = window.speechSynthesis.getVoices();
      console.log('Available voices:', voices.length);
      
      // Try to find a female voice
      let selectedVoice = null;
      
      // First, try to find voices with explicit gender property
      selectedVoice = voices.find(v => v.gender === 'female');
      
      // If not found, try to find by name patterns
      if (!selectedVoice) {
        selectedVoice = voices.find(v => 
          (v.name && v.name.toLowerCase().includes('female')) ||
          (v.voiceURI && v.voiceURI.toLowerCase().includes('female')) ||
          (v.name && v.name.toLowerCase().includes('woman')) ||
          (v.voiceURI && v.voiceURI.toLowerCase().includes('woman')) ||
          (v.name && v.name.toLowerCase().includes('soft'))
        );
      }
      
      // If still not found, try to find any English female-sounding voice
      if (!selectedVoice) {
        selectedVoice = voices.find(v => 
          v.lang.startsWith('en') && 
          (v.name.toLowerCase().includes('female') || 
           v.name.toLowerCase().includes('woman') ||
           v.name.toLowerCase().includes('girl'))
        );
      }
      
      // If still not found, use any English voice
      if (!selectedVoice) {
        selectedVoice = voices.find(v => v.lang.startsWith('en'));
      }
      
      // If still not found, use the default voice
      if (!selectedVoice && voices.length > 0) {
        selectedVoice = voices[0];
      }
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        console.log('Using voice:', selectedVoice.name);
      } else {
        console.warn('No suitable voice found, using default');
      }
      
      // Add event listeners for debugging
      utterance.onstart = () => console.log('Speech started');
      utterance.onend = () => console.log('Speech ended');
      utterance.onerror = (event) => console.error('Speech error:', event);
      
      window.speechSynthesis.speak(utterance);
      
    } catch (error) {
      console.error('Error in speech synthesis:', error);
    }
  }
  
  // Handle the case where voices might not be loaded yet
  const voices = window.speechSynthesis.getVoices();
  if (voices.length === 0) {
    console.log('Voices not loaded yet, waiting...');
    window.speechSynthesis.onvoiceschanged = () => {
      console.log('Voices loaded, speaking now...');
      speak();
    };
  } else {
    console.log('Voices already loaded, speaking immediately...');
    speak();
  }
}

export function speakText(text: string) {
  console.log('Attempting to speak:', text);
  
  // Check if speech synthesis is available
  if (!('speechSynthesis' in window)) {
    console.warn('Speech synthesis not supported in this browser');
    return;
  }
  
  // Cancel any ongoing speech
  window.speechSynthesis.cancel();
  
  // Simple fallback if the female voice function doesn't work
  try {
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => console.log('Speech started');
    utterance.onend = () => console.log('Speech ended');
    utterance.onerror = (event) => console.error('Speech error:', event);
    
    window.speechSynthesis.speak(utterance);
  } catch (error) {
    console.error('Error in speech synthesis:', error);
    // Fallback to the improved female voice function
    speakWithFemaleVoice(text);
  }
}

// Enhanced function for testing voice availability
export function testVoiceAvailability() {
  if (!('speechSynthesis' in window)) {
    console.error('Speech synthesis not supported');
    return false;
  }
  
  const voices = window.speechSynthesis.getVoices();
  console.log('Available voices:', voices);
  
  if (voices.length === 0) {
    console.log('No voices available yet, waiting for voices to load...');
    window.speechSynthesis.onvoiceschanged = () => {
      const loadedVoices = window.speechSynthesis.getVoices();
      console.log('Voices loaded:', loadedVoices);
    };
    return false;
  }
  
  return true;
}
